import { useLayoutEffect, useRef } from 'react';

const clamp = value => Math.max(0, Math.min(1, value));
const phase = (value, start, end) => {
  const t = clamp((value - start) / (end - start));
  return t * t * (3 - 2 * t);
};

export const useFormatScroll = () => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const section = ref.current;
    if (!section) return;
    const desktop = window.matchMedia('(min-width: 900px) and (min-height: 600px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const content = section.querySelector('.format-scene-content');
    const paper = section.querySelector('.practice-paper-layer');
    const pencil = section.querySelector('.practice-pencil-layer');
    const slip = section.querySelector('.assignment-slip');
    const copy = section.querySelector('.format-copy');
    const notes = [...section.querySelectorAll('.principle-notes li')];
    const footer = section.querySelector('.principles-return');
    const moving = [paper, pencil, slip, copy, ...notes, footer];
    let frame = 0, measureFrame = 0, scale = 1, x = 0, y = 0, enlargement = 1, keyboard = false;
    const reset = () => {
      section.classList.remove('format-scroll-active');
      content.style.removeProperty('transform');
      moving.forEach(element => { element.style.removeProperty('transform'); element.style.removeProperty('opacity'); });
    };
    const draw = () => {
      frame = 0;
      if (!desktop.matches || reduced.matches) return;
      const rect = section.getBoundingClientRect();
      const progress = keyboard ? 1 : clamp((88 - rect.top) / (section.offsetHeight - (window.innerHeight - 88)));
      const settle = phase(progress, .08, .53);
      paper.style.transform = `translate(${x * (1 - settle)}px,${y * (1 - settle)}px) scale(${1 + (enlargement - 1) * (1 - settle)})`;
      const arrive = phase(progress, .31, .64);
      pencil.style.opacity = phase(progress, .30, .40);
      pencil.style.transform = `translate(${-window.innerWidth / scale * (1 - arrive)}px,${90 * (1 - arrive)}px) rotate(${-42 * (1 - arrive)}deg)`;
      const assignment = phase(progress, .44, .71);
      slip.style.opacity = phase(progress, .44, .54);
      slip.style.transform = `translate(${window.innerWidth * .48 / scale * (1 - assignment)}px,${window.innerHeight * .65 / scale * (1 - assignment)}px) rotate(${3 + 14 * (1 - assignment)}deg)`;
      const reveal = phase(progress, .64, .82);
      copy.style.opacity = reveal;
      copy.style.transform = `translateY(${22 * (1 - reveal)}px)`;
      notes.forEach((note, index) => {
        const appear = phase(progress, .75 + index * .045, .88 + index * .045);
        note.style.opacity = appear;
        note.style.transform = `translateY(${28 * (1 - appear)}px) rotate(${index === 1 ? -1.3 : 0}deg)`;
      });
      footer.style.opacity = phase(progress, .92, 1);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(draw); };
    const measure = () => {
      reset();
      if (!desktop.matches || reduced.matches) return;
      section.classList.add('format-scroll-active');
      const available = window.innerHeight - 112;
      scale = Math.min(1, available / content.offsetHeight);
      content.style.transform = `scale(${scale})`;
      const bounds = paper.getBoundingClientRect();
      x = (window.innerWidth / 2 - (bounds.left + bounds.width / 2)) / scale;
      const stickyTop = section.querySelector('.format-sticky').getBoundingClientRect().top;
      y = (available / 2 - (bounds.top - stickyTop + bounds.height / 2)) / scale;
      enlargement = Math.min(window.innerWidth * .86 / bounds.width, available * .94 / bounds.height);
      draw();
    };
    const resize = () => {
      cancelAnimationFrame(measureFrame);
      measureFrame = requestAnimationFrame(measure);
    };
    const keys = event => {
      if (event.key === 'Tab' && section.contains(event.target)) { keyboard = true; schedule(); }
    };
    const focus = () => { keyboard = true; schedule(); };
    const pointer = () => { keyboard = false; schedule(); };
    const visible = () => { if (!document.hidden) resize(); };
    const observer = new ResizeObserver(resize);
    observer.observe(content);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', resize);
    desktop.addEventListener('change', resize);
    reduced.addEventListener('change', resize);
    section.addEventListener('keydown', keys);
    section.addEventListener('focusin', focus);
    section.addEventListener('pointerdown', pointer);
    document.addEventListener('visibilitychange', visible);
    measure();
    return () => {
      cancelAnimationFrame(frame); cancelAnimationFrame(measureFrame); observer.disconnect();
      window.removeEventListener('scroll', schedule); window.removeEventListener('resize', resize);
      desktop.removeEventListener('change', resize); reduced.removeEventListener('change', resize);
      section.removeEventListener('keydown', keys); section.removeEventListener('focusin', focus);
      section.removeEventListener('pointerdown', pointer); document.removeEventListener('visibilitychange', visible);
      reset();
    };
  }, []);
  return ref;
};
